import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import * as amplitude from "@amplitude/unified";
import { MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import "./ContactUs.css";

const offices = [
  {
    id: 6,
    name: "Indore, Madhya Pradesh, India",
    image:
      "https://cdn.pixabay.com/photo/2017/03/28/12/10/chairs-2181951_1280.jpg",
    address: "651, A Above Bandhan Bank, Mahalaxmi Nagar, Indore, Madhya Pradesh, India.",
  },
  {
    id: 5,
    name: "Jaipur, Rajasthan, India",
    image:
      "https://cdn.pixabay.com/photo/2017/03/28/12/09/chairs-2181939_1280.jpg",
    address: "6, Khandelwal Tower, Vidhyadhar Nagar Jaipur",
  },
];

const upcomingOffices = [
  {
    id: 1,
    name: "Dubai, UAE",
    image:
      "https://cdn.pixabay.com/photo/2016/10/16/10/30/office-space-1744805_640.jpg",
    address:
      "HDS Business Centre, 34th Cluster M, Jumeirah Lake Towers, Dubai, United Arab Emirates",
  },
  {
    id: 2,
    name: "Europe, Suriname",
    image:
      "https://cdn.pixabay.com/photo/2017/03/28/12/11/chairs-2181960_1280.jpg",
    address: "New Charlsburg #97 Paramaribo, Suriname",
  },
];

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        amplitude.track("Contact Form Submitted", {
          subject: formData.subject,
          has_phone: !!formData.phone,
        });
        alert("Message Sent Successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        amplitude.track("Contact Form Failed", {
          subject: formData.subject,
          status_code: response.status,
        });
        alert("Failed to send message.");
      }
    } catch (error) {
      amplitude.track("Contact Form Failed", {
        subject: formData.subject,
        error_message: error.message,
      });
      console.error("Error sending message:", error);
      alert("Failed to send message. Please check your network connection.");
    }

    setLoading(false);
  };

  return (
    <>
      <Navbar />

      <div className="video-container">
        <video autoPlay loop muted playsInline>
          <source
            src="https://cdn.pixabay.com/video/2020/08/12/46965-449623750_large.mp4"
            type="video/mp4"
          />
        </video>
      </div>

      <main className="main-container">
        {/* Hero Section */}
        <section className="hero-section">
          <h1 className="hero-title">Contact Us</h1>
          <p className="hero-description">
            We are committed to providing timely assistance and clear communication. Whether you have an enquiry, feedback, or require support, our team is ready to help.
          </p>
        </section>

        {/* Verified Business Contact Info Cards (Visit Us, Phone, Email) */}
        <section className="contact-cards-section" aria-label="Direct Contact Information">
          <div className="contact-cards-container">
            {/* Card 1: Visit Us */}
            <div className="contact-info-card contact-info-card--visit">
              <div className="contact-card-icon-wrap contact-card-icon--blue">
                <MapPin size={26} />
              </div>
              <h3 className="contact-card-title">Visit Us</h3>
              <p className="contact-card-address">
                651, A Above Bandhan Bank,<br />
                Mahalaxmi Nagar, Indore,<br />
                Madhya Pradesh, India.
              </p>
              <a
                href="https://maps.google.com/?q=651+A+Above+Bandhan+Bank+Mahalaxmi+Nagar+Indore+Madhya+Pradesh+India"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-card-cta contact-card-cta--map"
              >
                View on Google Maps <ExternalLink size={16} />
              </a>
            </div>

            {/* Card 2: Phone */}
            <div className="contact-info-card contact-info-card--phone">
              <div className="contact-card-icon-wrap contact-card-icon--green">
                <Phone size={26} />
              </div>
              <h3 className="contact-card-title">Phone</h3>
              <a href="tel:9039607555" className="contact-card-primary-link">
                9039607555
              </a>
              <p className="contact-card-meta">
                Mon - Sat: 9:00 AM - 7:00 PM IST
              </p>
              <span className="contact-card-badge">Support &amp; Sales Inquiries</span>
              <a href="tel:9039607555" className="contact-card-cta contact-card-cta--call">
                Call our team →
              </a>
            </div>

            {/* Card 3: Email */}
            <div className="contact-info-card contact-info-card--email">
              <div className="contact-card-icon-wrap contact-card-icon--purple">
                <Mail size={26} />
              </div>
              <h3 className="contact-card-title">Email</h3>
              <a href="mailto:contact@itsoftlab.com" className="contact-card-primary-link">
                contact@itsoftlab.com
              </a>
              <p className="contact-card-meta">
                Quick response within 24 hours
              </p>
              <span className="contact-card-badge">Enterprise &amp; Franchise Inquiries</span>
              <a href="mailto:contact@itsoftlab.com" className="contact-card-cta contact-card-cta--email">
                Send an email →
              </a>
            </div>
          </div>
        </section>

        {/* Contact Information & Locations */}
        <section className="contact-section">
          <div className="contatainer">

            {/* Our Office Locations */}
            <section className="locations-container">
              <h2 className="locations-title">Our Office Locations</h2>
              <div className="locations-grid">
                {offices.map((office) => (
                  <div key={office.id} className="location-card">
                    <div className="location-image-wrapper">
                      <img
                        src={office.image}
                        alt={office.name}
                        className="location-image"
                      />
                    </div>
                    <div className="location-card-body">
                      <h3 className="location-name">{office.name}</h3>
                      <div className="location-contact">
                        <MapPin size={18} className="location-pin-icon" />
                        <p className="location-address">{office.address}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Upcoming Office Locations */}
            <section className="locations-container">
              <h2 className="locations-title">Upcoming Offices</h2>
              <div className="locations-grid">
                {upcomingOffices.map((office) => (
                  <div key={office.id} className="location-card">
                    <div className="location-image-wrapper">
                      <img
                        src={office.image}
                        alt={office.name}
                        className="location-image"
                      />
                    </div>
                    <div className="location-card-body">
                      <h3 className="location-name">{office.name}</h3>
                      <div className="location-contact">
                        <MapPin size={18} className="location-pin-icon" />
                        <p className="location-address">{office.address}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form and Map */}
            <div className="contact-grid">
              {/* Contact Form */}
              <div className="contactaa">
                <h2 className="section-title">Send Us a Message</h2>
                {loading && (
                  <div className="loading-popup">
                    <div className="spinner"></div>
                    <p>Sending your message...</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="input-group">
                    <div className="contact-form-field">
                      <label htmlFor="contact-name">Your Name *</label>
                      <input
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. John Doe"
                        required
                      />
                    </div>
                    <div className="contact-form-field">
                      <label htmlFor="contact-email">Your Email *</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="e.g. john@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="contact-form-field">
                    <label htmlFor="contact-phone">Phone Number</label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="e.g. 9039607555"
                    />
                  </div>

                  <div className="contact-form-field">
                    <label htmlFor="contact-subject">Subject *</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Project Inquiry / Services"
                      required
                    />
                  </div>

                  <div className="contact-form-field">
                    <label htmlFor="contact-message">Message *</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder="Tell us about your project requirements..."
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send Message →"}
                  </button>
                </form>
              </div>

              {/* Location & Map */}
              <div className="contactbb">
                <h2 className="section-title">Our Location</h2>
                <div className="map-placeholder">
                  <a
                    href="https://maps.google.com/?q=651+A+Above+Bandhan+Bank+Mahalaxmi+Nagar+Indore+Madhya+Pradesh+India"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Click to view on Google Maps"
                    className="map-link-wrap"
                  >
                    <img
                      className="imggg"
                      src="/images/maps.png"
                      alt="ITSoftlab Office Location Map"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "images/maps.png";
                      }}
                    />
                  </a>
                </div>
                <p className="map-text">
                  651, A Above Bandhan Bank, Mahalaxmi Nagar, Indore, Madhya Pradesh, India. Centrally connected in a prominent business district with accessible public transit and visitor parking.
                </p>
                <a
                  href="https://maps.google.com/?q=651+A+Above+Bandhan+Bank+Mahalaxmi+Nagar+Indore+Madhya+Pradesh+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-btn-cta"
                >
                  View on Google Maps <ExternalLink size={16} />
                </a>
              </div>
            </div>

          </div>
        </section>

        {/* ===== Footer ===== */}
        <footer className="footer">
          <div className="footer-container">
            {/* Brand */}
            <div className="footer-section footer-brand">
              <div className="footer-logo-wrap">
                <img src="/images/log.png" alt="ITSoftLab Logo" className="footer-logo-img" />
              </div>
              <p>
                Empowering businesses with end-to-end 360° IT solutions,
                innovative digital products, and reliable technology services.
                We help organizations streamline operations, accelerate growth,
                and achieve digital transformation through cutting-edge software,
                web, mobile, cloud, and AI-driven solutions.
              </p>
              <p>
                Protect your data with top-notch cybersecurity measures. Awarded
                Best IT Company for consulting services and web development.
              </p>
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/aboutPage">About Us</Link></li>
                <li><Link to="/servicespage">Services</Link></li>
                <li><Link to="/careerpage">Careers</Link></li>
                <li><Link to="/contactus">Contact Us</Link></li>
                <li><Link to="/privacypage">Privacy Policy</Link></li>
                <li><Link to="/termpage">Terms &amp; Conditions</Link></li>
                <li><Link to="/products">Products</Link></li>
              </ul>
            </div>

            {/* Our Services */}
            <div className="footer-section">
              <h4>Our Services</h4>
              <ul>
                <li><Link to="/servicespage">Android / iOS App Development</Link></li>
                <li><Link to="/servicespage">Web App Development</Link></li>
                <li><Link to="/servicespage">Software Development</Link></li>
                <li><Link to="/servicespage">Cloud &amp; SaaS / PaaS / IaaS</Link></li>
                <li><Link to="/servicespage">Cyber Security</Link></li>
                <li><Link to="/servicespage">Disaster Recovery</Link></li>
                <li><Link to="/servicespage">AI / ML Solutions</Link></li>
                <li><Link to="/servicespage">DevOps &amp; Automation</Link></li>
              </ul>
            </div>

            {/* Our Products */}
            <div className="footer-section">
              <h4>Our Products</h4>
              <ul>
                <li><Link to="/crm">CRM</Link></li>
                <li><a href="https://hrmsm.com/home" target="_blank" rel="noopener noreferrer">HRMS</a></li>
                <li><Link to="/contactus">VMS</Link></li>
                <li><Link to="/contactus">ERP</Link></li>
                <li><Link to="/contactus">DMS</Link></li>
                <li><Link to="/contactus">LMS</Link></li>
                <li><Link to="/contactus">POS</Link></li>
                <li><Link to="/contactus">Marketing Automation</Link></li>
              </ul>
            </div>
          </div>

          {/* Contact Bar */}
          <div className="footer-contact-bar">
            <div className="footer-contact-list">
              <div className="footer-contact-row">
                <strong>📞 Phone</strong>
                <a href="tel:9039607555" className="footer-contact-link">9039607555</a>
              </div>
              <div className="footer-contact-row">
                <strong>🕒 Working Hours</strong>
                <span>Mon - Sat: 9:00 AM - 7:00 PM IST</span>
              </div>
              <div className="footer-contact-row">
                <strong>✉ Email</strong>
                <a href="mailto:contact@itsoftlab.com" className="footer-contact-link">contact@itsoftlab.com</a>
              </div>
            </div>
          </div>

          <hr className="footer-hr" />
          <p className="footer-bottom">© 2026 IT SOFTLAB. All rights reserved.</p>
        </footer>
      </main>
    </>
  );
}
